import {useCallback, useEffect, useState} from 'react'

const CACHE_STORAGE_KEY = 'tfi_static_catalog_cache_v1'
const DEFAULT_TTL_MS = 1000 * 60 * 60 * 12 // 12 hours

interface CacheEntry<T> {
  data: T
  lastFetched: number
}

const readCacheEntry = <T,>(key: string): CacheEntry<T> | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as Record<string, CacheEntry<T>>
    return parsed[key]
  } catch (error) {
    console.warn('Failed to read static catalog cache', error)
    return undefined
  }
}

const writeCacheEntry = <T,>(key: string, entry: CacheEntry<T>) => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    parsed[key] = entry
    window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(parsed))
  } catch (error) {
    console.warn('Failed to persist static catalog cache', error)
  }
}

export const invalidateCatalog = (key: string) => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (parsed[key]) {
      delete parsed[key]
      window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(parsed))
    }
  } catch (error) {
    console.warn('Failed to invalidate static catalog cache', error)
  }
}

export const clearStaticCatalogCache = () => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CACHE_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear static catalog cache', error)
  }
}

export interface StaticCatalogResult<T> {
  data?: T
  loading: boolean
  error?: Error
  refetch: () => Promise<void>
  invalidate: () => void
  lastFetched?: number
}

export const useStaticCatalog = <T,>(
  key: string,
  enabled: boolean,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): StaticCatalogResult<T> => {
  const [entry, setEntry] = useState<CacheEntry<T> | undefined>(() =>
    enabled ? readCacheEntry<T>(key) : undefined
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)

  const isExpired = () => {
    if (!entry) return true
    return Date.now() - entry.lastFetched > ttlMs
  }

  const executeFetch = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(undefined)
    try {
      const data = await fetcher()
      const nextEntry: CacheEntry<T> = {data, lastFetched: Date.now()}
      setEntry(nextEntry)
      writeCacheEntry(key, nextEntry)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [enabled, fetcher, key])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }

    if (!entry) {
      const cached = readCacheEntry<T>(key)
      if (cached) {
        setEntry(cached)
        return
      }
    }

    if (!entry || isExpired()) {
      executeFetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entry, executeFetch, key])

  const refetch = useCallback(async () => {
    await executeFetch()
  }, [executeFetch])

  const invalidate = useCallback(() => {
    if (typeof window === 'undefined') return
    try {
      const raw = window.localStorage.getItem(CACHE_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed[key]) {
        delete parsed[key]
        window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(parsed))
      }
    } catch (error) {
      console.warn('Failed to invalidate static catalog cache', error)
    }
    setEntry(undefined)
  }, [key])

  return {
    data: entry?.data,
    loading: enabled ? loading : false,
    error,
    refetch,
    invalidate,
    lastFetched: entry?.lastFetched,
  }
}
